import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import {
  TrendingUp,
  DollarSign,
  FileText,
  Briefcase,
  CheckCircle,
  Clock,
  AlertCircle,
  Calendar,
  Users,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const BusinessDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    revenue: {
      thisMonth: 0,
      lastMonth: 0,
      change: 0
    },
    jobs: {
      active: 0,
      scheduled: 0,
      completed: 0,
      todayScheduled: []
    },
    estimates: {
      pending: 0,
      thisMonth: 0,
      conversionRate: 0
    },
    invoices: {
      outstanding: 0,
      overdue: 0,
      overdueAmount: 0
    },
    recentActivity: []
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const firstDayNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const today = now.toISOString().split('T')[0];

      // Get jobs data
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          id,
          job_number,
          status,
          start_date,
          completion_date,
          total_amount,
          job_address,
          customers (
            first_name,
            last_name,
            company
          )
        `);

      if (jobsError) throw jobsError;

      // Get estimates data
      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select('id, status, created_at, total_amount');

      if (estimatesError) throw estimatesError;

      // Get invoices data
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('id, status, due_date, total_amount');

      if (invoicesError) throw invoicesError;

      // Calculate revenue
      const completedJobsThisMonth = (jobsData || []).filter(job =>
        job.status === 'Completed' &&
        job.completion_date &&
        new Date(job.completion_date) >= firstDayThisMonth &&
        new Date(job.completion_date) < firstDayNextMonth
      );

      const completedJobsLastMonth = (jobsData || []).filter(job =>
        job.status === 'Completed' &&
        job.completion_date &&
        new Date(job.completion_date) >= firstDayLastMonth &&
        new Date(job.completion_date) < firstDayThisMonth
      );

      const revenueThisMonth = completedJobsThisMonth.reduce((sum, job) =>
        sum + (parseFloat(job.total_amount) || 0), 0
      );

      const revenueLastMonth = completedJobsLastMonth.reduce((sum, job) =>
        sum + (parseFloat(job.total_amount) || 0), 0
      );

      const revenueChange = revenueLastMonth > 0
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100
        : 0;

      // Calculate jobs stats
      const activeJobs = (jobsData || []).filter(job => job.status === 'In Progress').length;
      const scheduledJobs = (jobsData || []).filter(job => job.status === 'Scheduled').length;
      const completedJobs = (jobsData || []).filter(job => job.status === 'Completed').length;

      const todayScheduled = (jobsData || []).filter(job =>
        job.status === 'Scheduled' &&
        job.start_date === today
      );

      // Calculate estimates stats
      const pendingEstimates = (estimatesData || []).filter(est => est.status === 'Pending').length;
      const estimatesThisMonth = (estimatesData || []).filter(est =>
        new Date(est.created_at) >= firstDayThisMonth
      ).length;

      const acceptedEstimates = (estimatesData || []).filter(est => est.status === 'Accepted').length;
      const conversionRate = estimatesData && estimatesData.length > 0
        ? (acceptedEstimates / estimatesData.length) * 100
        : 0;

      // Calculate invoices stats
      const outstandingInvoices = (invoicesData || []).filter(inv =>
        inv.status === 'Sent' || inv.status === 'Overdue'
      ).length;

      const overdueInvoices = (invoicesData || []).filter(inv =>
        inv.status === 'Overdue' || (inv.status === 'Sent' && inv.due_date && new Date(inv.due_date) < now)
      );

      const overdueAmount = overdueInvoices.reduce((sum, inv) =>
        sum + (parseFloat(inv.total_amount) || 0), 0
      );

      // Recent activity (last 5 completed jobs)
      const recentActivity = (jobsData || [])
        .filter(job => job.status === 'Completed' && job.completion_date)
        .sort((a, b) => new Date(b.completion_date) - new Date(a.completion_date))
        .slice(0, 5);

      setDashboardData({
        revenue: {
          thisMonth: revenueThisMonth,
          lastMonth: revenueLastMonth,
          change: revenueChange
        },
        jobs: {
          active: activeJobs,
          scheduled: scheduledJobs,
          completed: completedJobs,
          todayScheduled
        },
        estimates: {
          pending: pendingEstimates,
          thisMonth: estimatesThisMonth,
          conversionRate
        },
        invoices: {
          outstanding: outstandingInvoices,
          overdue: overdueInvoices.length,
          overdueAmount
        },
        recentActivity
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Business Dashboard</h1>
        <p className="text-gray-600 mt-1">
          Welcome back! Here's what's happening with your business today.
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Revenue Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <DollarSign size={24} />
            </div>
            {dashboardData.revenue.change !== 0 && (
              <div className={`flex items-center space-x-1 ${dashboardData.revenue.change >= 0 ? 'text-green-100' : 'text-red-200'}`}>
                {dashboardData.revenue.change >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                <span className="text-sm font-semibold">
                  {Math.abs(dashboardData.revenue.change).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          <h3 className="text-sm font-medium opacity-90">Revenue This Month</h3>
          <p className="text-3xl font-bold mt-2">{formatCurrency(dashboardData.revenue.thisMonth)}</p>
          <p className="text-xs opacity-75 mt-2">
            Last month: {formatCurrency(dashboardData.revenue.lastMonth)}
          </p>
        </div>

        {/* Active Jobs Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <Briefcase size={24} />
            </div>
          </div>
          <h3 className="text-sm font-medium opacity-90">Active Jobs</h3>
          <p className="text-3xl font-bold mt-2">{dashboardData.jobs.active}</p>
          <p className="text-xs opacity-75 mt-2">
            {dashboardData.jobs.scheduled} scheduled · {dashboardData.jobs.completed} completed
          </p>
        </div>

        {/* Pending Estimates Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <FileText size={24} />
            </div>
          </div>
          <h3 className="text-sm font-medium opacity-90">Pending Estimates</h3>
          <p className="text-3xl font-bold mt-2">{dashboardData.estimates.pending}</p>
          <p className="text-xs opacity-75 mt-2">
            {dashboardData.estimates.thisMonth} created this month · {dashboardData.estimates.conversionRate.toFixed(0)}% conversion
          </p>
        </div>

        {/* Outstanding Invoices Card */}
        <div className={`bg-gradient-to-br ${dashboardData.invoices.overdue > 0 ? 'from-red-500 to-red-600' : 'from-yellow-500 to-yellow-600'} rounded-lg shadow-lg p-6 text-white`}>
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <AlertCircle size={24} />
            </div>
          </div>
          <h3 className="text-sm font-medium opacity-90">Outstanding Invoices</h3>
          <p className="text-3xl font-bold mt-2">{dashboardData.invoices.outstanding}</p>
          <p className="text-xs opacity-75 mt-2">
            {dashboardData.invoices.overdue} overdue · {formatCurrency(dashboardData.invoices.overdueAmount)}
          </p>
        </div>
      </div>

      {/* Today's Schedule & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Today's Schedule */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Calendar className="mr-2 text-blue-600" size={24} />
              Today's Schedule
            </h2>
            <span className="text-sm text-gray-500">{formatDate(new Date())}</span>
          </div>

          {dashboardData.jobs.todayScheduled.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="mx-auto text-gray-400 mb-3" size={48} />
              <p className="text-gray-500">No jobs scheduled for today</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboardData.jobs.todayScheduled.map((job) => (
                <div key={job.id} className="border-l-4 border-blue-500 bg-blue-50 p-4 rounded">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">Job #{job.job_number}</p>
                      <p className="text-sm text-gray-600">{job.job_address}</p>
                      <p className="text-sm text-gray-700 mt-1">
                        {job.customers?.company || `${job.customers?.first_name} ${job.customers?.last_name}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{formatCurrency(job.total_amount)}</p>
                      <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded mt-1">
                        Scheduled
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="mr-2 text-green-600" size={24} />
            Recent Completions
          </h2>

          {dashboardData.recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="mx-auto text-gray-400 mb-3" size={48} />
              <p className="text-gray-500">No completed jobs yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboardData.recentActivity.map((job) => (
                <div key={job.id} className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">Job #{job.job_number}</p>
                      <p className="text-sm text-gray-600">{job.job_address}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Completed {formatDate(job.completion_date)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{formatCurrency(job.total_amount)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Stats</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{dashboardData.jobs.active + dashboardData.jobs.scheduled}</p>
            <p className="text-sm text-gray-600 mt-1">Total Active Work</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{dashboardData.jobs.completed}</p>
            <p className="text-sm text-gray-600 mt-1">Completed Jobs</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{dashboardData.estimates.conversionRate.toFixed(0)}%</p>
            <p className="text-sm text-gray-600 mt-1">Win Rate</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardData.revenue.thisMonth)}</p>
            <p className="text-sm text-gray-600 mt-1">Monthly Revenue</p>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {(dashboardData.invoices.overdue > 0 || dashboardData.estimates.pending > 5) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
          <div className="flex items-start">
            <AlertCircle className="text-yellow-600 mt-0.5 flex-shrink-0" size={20} />
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-yellow-800">Attention Required</h3>
              <div className="mt-2 text-sm text-yellow-700 space-y-1">
                {dashboardData.invoices.overdue > 0 && (
                  <p>• {dashboardData.invoices.overdue} overdue invoice{dashboardData.invoices.overdue > 1 ? 's' : ''} totaling {formatCurrency(dashboardData.invoices.overdueAmount)}</p>
                )}
                {dashboardData.estimates.pending > 5 && (
                  <p>• {dashboardData.estimates.pending} pending estimates need follow-up</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDashboard;
